import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NumericInput } from '@/components/ui/numeric-input'
import { EmailBodyEditor } from './EmailBodyEditor'
import { VariableDropdown } from './VariableDropdown'
import { formatTokenText, renderTemplate, type TemplateVariable } from '@/lib/templateVariables'
import { resolveStepSubject } from '@/lib/sequences'
import { stripHtml } from '@/lib/utils'
import { Clock, Paperclip, X, Loader2, Eye, EyeOff, Info } from 'lucide-react'
import {
  useStepAttachments,
  useUploadStepAttachment,
  useDeleteStepAttachment,
  useNormalizeEmailHtml,
} from '@/hooks/useApi'

export interface StepFormData {
  subject: string
  body_html: string
  delay_days: number
  delay_hours: number
}

interface StepFormProps {
  initialData?: StepFormData
  stepNumber: number
  stepId?: string
  /** Oggetto (template) del primo step: i follow-up rispondono con `Re:` (#264). */
  firstStepSubject?: string
  onSave: (data: StepFormData) => void
  onCancel: () => void
  isNew?: boolean
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function StepForm({
  initialData,
  stepNumber,
  stepId,
  firstStepSubject,
  onSave,
  onCancel,
  isNew,
}: StepFormProps) {
  const { t } = useTranslation(['sequences', 'common'])
  const [subject, setSubject] = useState(initialData?.subject ?? '')
  const [bodyHtml, setBodyHtml] = useState(initialData?.body_html ?? '')
  const [delayDays, setDelayDays] = useState(initialData?.delay_days ?? 0)
  const [delayHours, setDelayHours] = useState(initialData?.delay_hours ?? 0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const subjectRef = useRef<HTMLInputElement>(null)

  const { data: attachments } = useStepAttachments(stepId)
  const uploadAttachment = useUploadStepAttachment()
  const deleteAttachment = useDeleteStepAttachment()

  const [showPreview, setShowPreview] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewError, setPreviewError] = useState(false)
  const normalizeHtml = useNormalizeEmailHtml()

  const togglePreview = () => {
    if (showPreview) {
      setShowPreview(false)
      return
    }
    setPreviewError(false)
    normalizeHtml.mutate(
      { html: renderTemplate(bodyHtml) },
      {
        onSuccess: (data) => {
          setPreviewHtml(data.html)
          setShowPreview(true)
        },
        onError: () => setPreviewError(true),
      },
    )
  }

  const isFirstStep = stepNumber === 1
  // Oggetto del thread che sostituisce quello del follow-up (#264)
  const threadSubject = isFirstStep ? undefined : firstStepSubject
  const canSave = (isFirstStep ? subject.trim() !== '' : true) && stripHtml(bodyHtml).trim() !== ''

  const handleSave = () => {
    if (!canSave) return
    onSave({
      subject,
      body_html: bodyHtml,
      delay_days: delayDays,
      delay_hours: delayHours,
    })
  }

  const handleInsertSubjectVariable = (variable: TemplateVariable, fallback?: string) => {
    const pos = subjectRef.current?.selectionStart ?? subject.length
    const token = formatTokenText(variable.key, fallback)
    const next = subject.slice(0, pos) + token + subject.slice(pos)
    setSubject(next)
    requestAnimationFrame(() => {
      subjectRef.current?.setSelectionRange(pos + token.length, pos + token.length)
      subjectRef.current?.focus()
    })
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || !stepId) return
    for (const file of Array.from(files)) {
      uploadAttachment.mutate({ stepId, file })
    }
    e.target.value = ''
  }

  return (
    <div className="border rounded-lg p-4 bg-muted/50">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">{t('step.heading', { number: stepNumber })}</h4>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span>{t('step.delayLabel')}</span>
          <NumericInput
            value={delayDays}
            onChange={setDelayDays}
            min={0}
            className="w-16 h-8"
          />
          <span>{t('step.days')}</span>
          <NumericInput
            value={delayHours}
            onChange={setDelayHours}
            min={0}
            max={23}
            className="w-16 h-8"
          />
          <span>{t('step.hours')}</span>
        </div>
      </div>
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label>{t('step.subject')}</Label>
            <div className="ml-auto">
              <VariableDropdown onInsert={handleInsertSubjectVariable} />
            </div>
          </div>
          <Input
            ref={subjectRef}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={isFirstStep ? t('step.subjectPlaceholder') : t('step.subjectPlaceholderFollowUp')}
          />
          {threadSubject && (
            <p role="note" className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5 shrink-0 mt-px" />
              {t('step.followUpSubjectNotice', {
                subject: resolveStepSubject(subject, threadSubject),
              })}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label>{t('step.body')}</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              onClick={togglePreview}
              disabled={normalizeHtml.isPending}
            >
              {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {showPreview ? t('step.hidePreview') : t('step.showPreview')}
            </Button>
          </div>
          <EmailBodyEditor
            value={bodyHtml}
            onChange={setBodyHtml}
            placeholder={t('step.bodyPlaceholder')}
          />
          {showPreview && (
            <iframe
              title={t('step.previewTitle')}
              sandbox=""
              srcDoc={previewHtml}
              className="w-full min-h-[200px] border rounded-md bg-white"
            />
          )}
          {previewError && (
            <p role="alert" aria-live="polite" className="text-xs text-destructive">
              {t('step.previewError')}
            </p>
          )}
        </div>

        {/* Attachments */}
        {stepId && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t('step.attachments')}</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadAttachment.isPending}
              >
                {uploadAttachment.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Paperclip className="h-3.5 w-3.5" />
                )}
                {t('step.addFile')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
            {attachments && attachments.length > 0 && (
              <div className="space-y-1">
                {attachments.map((att) => (
                  <div
                    key={att.id}
                    className="flex items-center justify-between px-3 py-1.5 border rounded text-sm bg-background"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate">{att.filename}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatFileSize(att.file_size)}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 shrink-0"
                      onClick={() => deleteAttachment.mutate({ stepId, attachmentId: att.id })}
                      disabled={deleteAttachment.isPending}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {isNew ? t('step.saveStep') : t('common:save')}
          </Button>
        </div>
      </div>
    </div>
  )
}
